/* Studio · Offers & Settings · Admin Tools — maintenance & technical actions
   moved out of the daily-work views (email pipeline test, cron, Stripe ops). */
import React, { useState } from "react";
import axios from "axios";
import { Loader2, AlertTriangle } from "lucide-react";
import { toast } from "sonner";
import { studioHeaders } from "@/lib/studioAuth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function Tool({ title, desc, danger, action, actionLabel, testid }) {
    const [busy, setBusy] = useState(false);
    const run = async () => {
        if (danger && !window.confirm(`${title} — are you sure? This is a maintenance action.`)) return;
        setBusy(true);
        try { await action(); } catch (e) { toast.error(e?.response?.data?.detail || `${title} failed`); }
        finally { setBusy(false); }
    };
    return (
        <div className="rounded-2xl bg-white border border-border p-5 flex items-start justify-between gap-4 flex-wrap">
            <div className="max-w-md">
                <p className="font-medium text-cafe-espresso flex items-center gap-2">
                    {danger && <AlertTriangle className="h-3.5 w-3.5 text-red-600" />} {title}
                </p>
                <p className="text-[12.5px] text-cafe-mocha mt-1">{desc}</p>
            </div>
            <button onClick={run} disabled={busy} data-testid={testid}
                className={`rounded-full px-5 py-2 text-xs font-bold uppercase tracking-[0.1em] border transition-colors disabled:opacity-50 ${danger ? "border-red-300 text-red-700 hover:bg-red-50" : "border-cafe-espresso/20 text-cafe-espresso hover:bg-cafe-latte"}`}>
                {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin inline" /> : actionLabel}
            </button>
        </div>
    );
}

export default function AdminToolsTab() {
    const [reconcile, setReconcile] = useState(null);
    const [cls, setCls] = useState(null);
    const [clsBusy, setClsBusy] = useState(false);
    const loadCls = async () => {
        const r = await axios.get(`${API}/admin/classification/preview`, { headers: studioHeaders() });
        setCls(r.data.map((x) => ({ ...x, chosen: x.proposed_class })));
    };
    const applyCls = async () => {
        if (!window.confirm(`Apply classification to ${cls.length} records? Records are updated, never deleted.`)) return;
        setClsBusy(true);
        try {
            const { data } = await axios.post(`${API}/admin/classification/apply`,
                { items: cls.map((x) => ({ coll: x.coll, id: x.id, record_class: x.chosen })) },
                { headers: studioHeaders() });
            toast.success(`Classification applied to ${data.applied} records`);
            setCls(null);
        } finally { setClsBusy(false); }
    };
    return (
        <div className="space-y-4 max-w-3xl" data-testid="admin-tools">
            <p className="text-sm text-cafe-mocha">
                Technical &amp; maintenance actions. These do not belong in daily work — use with care.
            </p>

            <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold text-cafe-olive pt-2">Record classification</h3>
            <Tool title="Record Classification Review" testid="admin-classification"
                desc="One-time review: label every lead, inquiry, project, order and subscription as LIVE / DEMO / TEST / INTERNAL. Only LIVE records count in the Overview."
                actionLabel="Load review"
                action={loadCls} />
            {cls && (
                <div className="rounded-2xl bg-white border border-border p-4" data-testid="classification-review">
                    <div className="max-h-96 overflow-y-auto space-y-1.5">
                        {cls.map((r, i) => (
                            <div key={`${r.coll}-${r.id}-${i}`} className="flex items-center gap-2 flex-wrap text-[12px] border-b border-border/60 pb-1.5">
                                <span className="w-24 shrink-0 text-cafe-mocha/70 text-[10px] uppercase">{r.type}</span>
                                <span className="flex-1 min-w-[120px] font-medium text-cafe-espresso truncate">{r.name}</span>
                                <span className="text-cafe-mocha truncate max-w-[150px]">{r.email}</span>
                                <span className="text-[10px] text-cafe-mocha/70">{r.status}{r.value_rm > 0 ? ` · RM${r.value_rm}` : ""}</span>
                                <select value={r.chosen} data-testid={`cls-select-${i}`}
                                    onChange={(e) => setCls(cls.map((x, j) => j === i ? { ...x, chosen: e.target.value } : x))}
                                    className="rounded-md border border-border bg-white px-1.5 py-0.5 text-[11px]">
                                    {["live", "demo", "test", "internal", "unclassified"].map((v) => <option key={v} value={v}>{v.toUpperCase()}</option>)}
                                </select>
                            </div>
                        ))}
                    </div>
                    <button onClick={applyCls} disabled={clsBusy} data-testid="cls-apply-btn"
                        className="mt-3 rounded-full bg-cafe-olive text-cafe-cream px-6 py-2 text-xs font-bold uppercase tracking-[0.1em] disabled:opacity-50">
                        {clsBusy ? "Applying…" : `Confirm & apply (${cls.length})`}
                    </button>
                </div>
            )}

            <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold text-cafe-olive pt-2">Email operations</h3>
            <Tool title="Test email pipeline" testid="admin-test-email"
                desc="Sends the full kit-order email sequence to an address of your choice to verify Resend delivery."
                actionLabel="Send test"
                action={async () => {
                    const to = window.prompt("Send test emails to:", "");
                    if (!to) return;
                    const { data } = await axios.post(`${API}/orders/test-email`, { to }, { headers: studioHeaders() });
                    toast.success(`Test emails sent to ${to}${data?.sent ? ` (${data.sent})` : ""}`);
                }} />

            <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold text-cafe-olive pt-2">Scheduler</h3>
            <Tool title="Run shoot-order cron now" testid="admin-run-cron"
                desc="Runs the hourly reminder / 24h-delay check for client projects immediately instead of waiting."
                actionLabel="Run now"
                action={async () => {
                    const { data } = await axios.post(`${API}/admin/shoot-orders/cron-tick`, {}, { headers: studioHeaders() });
                    toast.success(`Cron tick done${data?.processed != null ? ` · processed ${data.processed}` : ""}`);
                }} />

            <h3 className="text-[11px] uppercase tracking-[0.2em] font-bold text-cafe-olive pt-2">Stripe operations</h3>
            <Tool title="Check subscription reconciliation" testid="admin-reconcile"
                desc="Compares local subscription rows against your current Stripe account and reports orphans."
                actionLabel="Check status"
                action={async () => {
                    const r = await axios.get(`${API}/subscriptions/admin/reconcile-status`, { headers: studioHeaders() });
                    setReconcile(r.data);
                    toast.success("Reconcile status loaded");
                }} />
            {reconcile && (
                <pre className="rounded-2xl bg-white border border-border p-4 text-[11.5px] text-cafe-espresso overflow-x-auto" data-testid="admin-reconcile-result">
                    {JSON.stringify(reconcile, null, 2)}
                </pre>
            )}
            <Tool title="Clean orphaned subscriptions" danger testid="admin-clean-orphans"
                desc="Removes local rows that no longer exist in your current Stripe account (old keys / test data). Live Stripe subscriptions are never touched."
                actionLabel="Clean orphans"
                action={async () => {
                    const r = await axios.post(`${API}/subscriptions/admin/clean-orphans`, {}, { headers: studioHeaders(), timeout: 120000 });
                    toast.success(`Clean complete — removed ${r.data?.removed ?? 0} orphans`);
                }} />
        </div>
    );
}
